import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  contentChild,
  contentChildren,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DatePipe, JsonPipe, NgTemplateOutlet } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { ActionMenu, ActionMenuAnchor, ActionMenuItem } from '../action-menu/action-menu';
import { Icon } from '../icon/icon';
import { Button } from '../button/button';
import { LanguageService } from '../../../core/i18n/language.service';
import { ViewportService } from '../../../core/services/viewport.service';
import { GridCellContext, GridCellDirective } from './directives/grid-cell.directive';
import { GridDetailDirective } from './directives/grid-detail.directive';
import {
  GridBulkAction,
  GridColumn,
  GridDensity,
  GridPrimaryAction,
  GridRowAction,
  GridSort,
  GridStatus,
  GridTab,
} from './models/grid.model';

/** Per-type default column width (px), used when a column omits `width`. */
const DEFAULT_WIDTH: Record<string, number> = {
  thumbnail: 110,
  status: 150,
  date: 170,
  boolean: 110,
  json: 120,
  text: 200,
  template: 200,
};
/** Per-type minimum column width (px): the floor a column can compress to
 *  before the table starts scrolling horizontally. Deliberately well below the
 *  defaults above — those are preferred sizes for roomy layouts, not the point
 *  at which content stops being readable. */
const MIN_WIDTH: Record<string, number> = {
  thumbnail: 110,
  status: 100,
  date: 130,
  boolean: 70,
  json: 90,
  text: 110,
  template: 110,
};
const SELECT_COL_WIDTH = 50;
const ACTIONS_COL_WIDTH = 150;
/** Deterministic text-bone widths (%) for skeleton rows, indexed by a row/col
 *  stride — stable across change detection (no Math.random), organic to the eye. */
const BONE_WIDTHS = [85, 55, 70, 40, 90, 60, 75, 50];
/** Bones only need to fill the fold, not mirror a 100-row page. */
const SKELETON_MAX_ROWS = 10;
/** Horizontal inset (px-4 both sides) around the table region — border-box, so
 *  the min-width must cover it on top of the column bases. */
const TABLE_GUTTER = 32;

/** A row prepared for the template — value + rendering flags resolved once. */
interface RenderCell {
  key: string;
  flexCss: string;
  minWidthPx: number;
  align: 'left' | 'center' | 'right';
  type: GridColumn['type'];
  textVariant: 'strong' | 'normal' | 'muted';
  value: unknown;
  status: GridStatus | null;
  thumbnailSrc: string | null;
  templateName: string;
}
interface RenderRow<T> {
  id: string;
  row: T;
  selected: boolean;
  cells: RenderCell[];
}
/** A placeholder cell for one skeleton bone — real column geometry, no data. */
interface SkeletonCell {
  key: string;
  flexCss: string;
  minWidthPx: number;
  align: 'left' | 'center' | 'right';
  type: GridColumn['type'];
  boneWidthPct: number;
}

/**
 * UniversalGrid (`app-grid`) — the extensible, signal-based data grid described
 * in docs / the "Universal table grid view" design. Server-side by contract:
 * the grid renders the page it's given and *emits* sort/page/search/selection
 * intents; the parent fetches. Every feature past columns+data is opt-in.
 *
 * Rendering: built-in cell types (text/status/thumbnail/date/boolean/json) plus
 * projected `<ng-template gridCell="key">` for anything custom — no [innerHTML].
 *
 * Conventions mirror the modern components (navbar, user-card): standalone,
 * OnPush, signal I/O, host-binding click-outside + Escape, takeUntilDestroyed.
 */
@Component({
  selector: 'app-grid',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ActionMenu, Button, Icon, TranslocoDirective, NgTemplateOutlet, DatePipe, JsonPipe],
  templateUrl: './grid.html',
  styleUrl: './grid.css',
  host: {
    // Fill the height given by the parent (e.g. app-grid-page's flex-1 slot) so
    // the internal sticky-header / scroll-body / pinned-pagination layout works.
    class: 'block h-full',
    '(document:mousedown)': 'onDocumentMouseDown($event)',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class Grid<T = unknown> {
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);

  // ── Data (parent-owned) ──────────────────────────────────────────────
  readonly data = input<readonly T[]>([]);
  readonly columns = input.required<GridColumn<T>[]>();
  /** Stable id extractor; drives selection + @for tracking. */
  readonly rowId = input.required<(row: T) => string>();
  readonly loading = input(false);

  // ── Pagination (parent-owned current; grid emits requests) ───────────
  readonly page = input(0); // 0-based
  readonly pageSize = input(20);
  readonly totalElements = input(0);
  readonly totalPages = input(0);
  readonly pageSizeOptions = input<number[]>([10, 20, 50, 100]);

  // ── Sort (parent-owned) ──────────────────────────────────────────────
  readonly sort = input<GridSort | null>(null);

  // ── Optional feature configs ─────────────────────────────────────────
  /** Hides the entire tabs/toolbar row (tabs, search, density, columns,
   *  export, primary action) — for a grid embedded inside another page's own
   *  chrome, where that row would otherwise render near-empty. Everything it
   *  would have controlled just takes its default (e.g. `density`). */
  readonly showToolbar = input(true);
  readonly tabs = input<GridTab[]>([]);
  readonly activeTabId = input<string | null>(null);
  readonly searchable = input(false);
  readonly searchDebounceMs = input(300);
  readonly rowActions = input<GridRowAction<T>[]>([]);
  readonly bulkActions = input<GridBulkAction[]>([]);
  readonly primaryAction = input<GridPrimaryAction | null>(null);
  readonly exportable = input(false);
  readonly selectable = input(false);
  readonly detailEnabled = input(false);
  /**
   * How the detail panel presents:
   * - 'overlay' (default): a slide-in aside that only appears once a row is
   *   selected and can be dismissed — for occasional inspection (devices, config).
   * - 'split': a persistent side-by-side pane, always laid out next to the grid,
   *   showing an empty prompt until a row is selected — for master/detail screens
   *   (operations). No slide animation, no close button.
   */
  readonly detailMode = input<'overlay' | 'split'>('overlay');
  /**
   * Width of the detail pane in px. In both modes the detail is fixed-width; in
   * split mode the master (grid) flexes to fill the remaining space, so extra
   * width on wide screens goes to the master rather than sprawling the detail.
   */
  readonly detailWidth = input(372);
  /**
   * Lets a master/detail parent drive the detail selection (e.g. auto-selecting
   * the first row so a split pane is never empty on load). When this changes to
   * a non-null row the grid opens its detail on that row — highlighting it and,
   * in split mode, filling the pane. `null` leaves the internal state untouched
   * so the user's own click selection isn't clobbered. The grid still owns
   * `detailRow`; this is a one-way seed, not a two-way binding.
   */
  readonly selectedRow = input<T | null>(null);

  // ── Two-way state ────────────────────────────────────────────────────
  readonly density = model<GridDensity>('compact');
  readonly selectedIds = model<readonly string[]>([]);

  // ── Outputs ──────────────────────────────────────────────────────────
  readonly sortChange = output<GridSort>();
  readonly pageChange = output<number>(); // 0-based
  readonly pageSizeChange = output<number>();
  readonly searchChange = output<string>(); // debounced
  readonly tabChange = output<string>();
  readonly rowClick = output<T>();
  readonly rowDblClick = output<T>();
  readonly rowActionClick = output<{ actionId: string; row: T }>();
  readonly bulkActionClick = output<{ actionId: string; selectedIds: readonly string[] }>();
  readonly primaryActionClick = output<void>();
  readonly exportCsv = output<void>();

  // ── Projected templates ──────────────────────────────────────────────
  readonly cellTemplates = contentChildren(GridCellDirective);
  readonly detailTemplate = contentChild(GridDetailDirective);

  // ── Grid-owned state ─────────────────────────────────────────────────
  protected readonly hiddenColumns = signal<ReadonlySet<string>>(new Set());
  protected readonly openMenuRowId = signal<string | null>(null);
  /** Cursor coords when the menu was opened via right-click; null = at the ⋮. */
  protected readonly menuAnchor = signal<ActionMenuAnchor | null>(null);
  protected readonly columnMenuOpen = signal(false);
  protected readonly detailRow = signal<T | null>(null);
  protected readonly searchTerm = signal('');

  /** Id of the row whose detail panel is open, for the row highlight. */
  protected readonly detailRowId = computed(() => {
    const row = this.detailRow();
    return row === null ? null : this.rowId()(row);
  });

  private readonly viewport = inject(ViewportService);

  /** Below lg a fixed-width side panel doesn't fit — every detail mode
   *  becomes a full-width bottom sheet instead. */
  private readonly compactDetail = this.viewport.isCompactDetail;

  /** Column bases scale down with the viewport so more of the table fits
   *  before horizontal scrolling kicks in: ~4/5 width on tablets, ~2/3 on
   *  phones. Columns still flex to fill whatever space is available — this
   *  only lowers the floor they can shrink to (and fixed thumbnails). */
  private readonly columnScale = computed(() =>
    this.viewport.isMobile() ? 0.65 : this.viewport.isCompactDetail() ? 0.8 : 1,
  );

  /** Split mode: the detail pane is always laid out (persistent side-by-side). */
  protected readonly showSplitDetail = computed(
    () => this.detailEnabled() && this.detailMode() === 'split' && !this.compactDetail(),
  );
  /** Overlay mode: the detail aside only shows once a row is selected. */
  protected readonly showOverlayDetail = computed(
    () =>
      this.detailEnabled() &&
      this.detailMode() === 'overlay' &&
      !this.compactDetail() &&
      this.detailRow() !== null,
  );
  /** Compact viewports: the selected row's detail as a dismissable sheet. */
  protected readonly showSheetDetail = computed(
    () => this.detailEnabled() && this.compactDetail() && this.detailRow() !== null,
  );

  /** Side-panel width, clamped so wide panels (operations passes 800px) never
   *  starve the master table on smaller desktops. The percentage resolves
   *  against the master+detail flex row — NOT the viewport, which would ignore
   *  the sidebar and gutters and over-allocate on small screens. */
  protected readonly detailWidthCss = computed(() => `min(${this.detailWidth()}px, 45%)`);

  // Re-evaluate translated chrome strings on language switch (user-card pattern).
  private readonly activeLang = this.languageService.current;

  constructor() {
    // Debounced search → single output. distinctUntilChanged avoids refetching
    // on no-op keystrokes (e.g. moving the caret).
    toObservable(this.searchTerm)
      .pipe(
        debounceTime(this.searchDebounceMs()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((term) => this.searchChange.emit(term));

    // Parent-driven detail selection (e.g. auto-select first row). Mirrors the
    // seeded row into the grid's own detail state so the highlight + split pane
    // follow; null is ignored so it never clears a user's live selection.
    effect(() => {
      const seeded = this.selectedRow();
      if (seeded !== null) this.detailRow.set(seeded);
    });
  }

  // ── Column widths ────────────────────────────────────────────────────

  /** The flex-basis for a column: its own `width`, then a per-type default,
   *  scaled down on compact viewports. */
  private widthFor(col: GridColumn<T>): number {
    const base = col.width ?? DEFAULT_WIDTH[col.type] ?? DEFAULT_WIDTH['text'];
    return Math.round(base * this.columnScale());
  }

  /** The floor a column can shrink to: its own `minWidth`, then the per-type
   *  minimum, scaled with the viewport and never above the preferred basis. */
  private minFor(col: GridColumn<T>): number {
    if (col.type === 'thumbnail') return this.widthFor(col); // fixed, can't shrink
    const min = col.minWidth ?? MIN_WIDTH[col.type] ?? MIN_WIDTH['text'];
    return Math.min(this.widthFor(col), Math.round(min * this.columnScale()));
  }

  /** Thumbnails are fixed; every other column flexes to fill the container and
   *  shrinks with it (so opening the detail panel reflows columns narrower). */
  private flexFor(col: GridColumn<T>): string {
    const basis = this.widthFor(col);
    return col.type === 'thumbnail' ? `0 0 ${basis}px` : `1 1 ${basis}px`;
  }

  /** Columns visible under the current visibility set + density. */
  protected readonly visibleColumns = computed<GridColumn<T>[]>(() => {
    const hidden = this.hiddenColumns();
    const compact = this.density() === 'compact';
    return this.columns().filter((c) => {
      if (c.alwaysVisible) return true;
      if (hidden.has(c.key)) return false;
      if (compact && c.hideOnCompact) return false;
      return true;
    });
  });

  /** Columns that can be toggled in the visibility menu (excludes pinned). */
  protected readonly toggleableColumns = computed(() =>
    this.columns().map((c) => ({
      key: c.key,
      label: c.label,
      locked: !!c.alwaysVisible,
      visible: !this.hiddenColumns().has(c.key),
    })),
  );

  /** Minimum width the flex-row table can shrink to before scrolling: the sum
   *  of every column's FLOOR (not its preferred basis) + the fixed
   *  select/actions gutters. Columns compress from their basis toward their
   *  floor first; only below this total does horizontal scrolling start. */
  protected readonly tableMinWidth = computed(() => {
    const cols = this.visibleColumns().reduce((sum, c) => sum + this.minFor(c), 0);
    return (
      cols +
      TABLE_GUTTER +
      (this.selectable() ? SELECT_COL_WIDTH : 0) +
      (this.rowActions().length ? this.actionsColWidth() : 0)
    );
  });

  /** The 50px select checkbox stays full size — it's a tap target. */
  protected readonly selectColWidth = SELECT_COL_WIDTH;
  /** Actions column scales with the viewport like data columns (floored so
   *  the ⋮ menu button and one inline action always fit). */
  protected readonly actionsColWidth = computed(() =>
    Math.max(90, Math.round(ACTIONS_COL_WIDTH * this.columnScale())),
  );

  /** Header column view-models (label + sort state + width). */
  protected readonly headerColumns = computed(() => {
    const sort = this.sort();
    return this.visibleColumns().map((c) => ({
      key: c.key,
      label: c.label,
      align: c.align ?? 'left',
      flexCss: this.flexFor(c),
      minWidthPx: this.minFor(c),
      sortable: !!c.sortable,
      sortAsc: sort?.sortBy === (c.sortKey ?? c.key) && sort?.sortDirection === 'ASC',
      sortDesc: sort?.sortBy === (c.sortKey ?? c.key) && sort?.sortDirection === 'DESC',
    }));
  });

  // ── Rows ─────────────────────────────────────────────────────────────

  private cellValue(col: GridColumn<T>, row: T): unknown {
    return col.value ? col.value(row) : (row as Record<string, unknown>)[col.key];
  }

  protected readonly renderRows = computed<RenderRow<T>[]>(() => {
    const cols = this.visibleColumns();
    const id = this.rowId();
    const selected = new Set(this.selectedIds());
    return this.data().map((row) => ({
      id: id(row),
      row,
      selected: selected.has(id(row)),
      cells: cols.map((c) => {
        const value = this.cellValue(c, row);
        return {
          key: c.key,
          flexCss: this.flexFor(c),
          minWidthPx: this.minFor(c),
          align: c.align ?? 'left',
          type: c.type,
          textVariant: c.textVariant ?? (c.key === cols[0]?.key ? 'strong' : 'normal'),
          value,
          status: c.status ? c.status(value, row) : null,
          thumbnailSrc: c.thumbnailSrc ? (c.thumbnailSrc(row) ?? null) : null,
          templateName: c.template ?? c.key,
        } satisfies RenderCell;
      }),
    }));
  });

  // ── Loading states ───────────────────────────────────────────────────

  /** Cold load: loading with nothing on screen → skeleton bone rows. */
  protected readonly showSkeleton = computed(
    () => this.loading() && this.data().length === 0,
  );
  /** Refetch (sort/page/search): loading over existing rows → keep them,
   *  dimmed and inert, until fresh data lands. No layout jump. */
  protected readonly isRefetching = computed(
    () => this.loading() && this.data().length > 0,
  );

  /** Bone rows mirroring the visible column layout. Each cell reuses the real
   *  flex/min-width so nothing shifts when data replaces the skeleton. */
  protected readonly skeletonRows = computed<SkeletonCell[][]>(() => {
    const cols = this.visibleColumns();
    const count = Math.min(this.pageSize(), SKELETON_MAX_ROWS);
    return Array.from({ length: count }, (_, r) =>
      cols.map((c, i) => ({
        key: c.key,
        flexCss: this.flexFor(c),
        minWidthPx: this.minFor(c),
        align: c.align ?? 'left',
        type: c.type,
        boneWidthPct: BONE_WIDTHS[(r * 5 + i * 3) % BONE_WIDTHS.length],
      })),
    );
  });

  /** key → projected cell template, resolved from content children. */
  protected readonly cellTemplateMap = computed(() => {
    const map = new Map<string, GridCellDirective<T>>();
    for (const dir of this.cellTemplates()) {
      map.set(dir.gridCell(), dir as GridCellDirective<T>);
    }
    return map;
  });

  protected cellContext(row: T, value: unknown): GridCellContext<T> {
    return { $implicit: row, row, value };
  }

  // ── Selection ────────────────────────────────────────────────────────

  private readonly pageIds = computed(() => {
    const id = this.rowId();
    return this.data().map((r) => id(r));
  });

  protected readonly allSelected = computed(() => {
    const ids = this.pageIds();
    if (!ids.length) return false;
    const sel = new Set(this.selectedIds());
    return ids.every((i) => sel.has(i));
  });

  protected readonly someSelected = computed(() => {
    const sel = new Set(this.selectedIds());
    return this.pageIds().some((i) => sel.has(i)) && !this.allSelected();
  });

  protected readonly selectedCount = computed(() => this.selectedIds().length);

  protected toggleRow(id: string, event?: Event): void {
    event?.stopPropagation();
    const sel = new Set(this.selectedIds());
    sel.has(id) ? sel.delete(id) : sel.add(id);
    this.selectedIds.set([...sel]);
  }

  protected toggleSelectAll(): void {
    const ids = this.pageIds();
    const sel = new Set(this.selectedIds());
    if (this.allSelected()) {
      ids.forEach((i) => sel.delete(i));
    } else {
      ids.forEach((i) => sel.add(i));
    }
    this.selectedIds.set([...sel]);
  }

  protected clearSelection(): void {
    this.selectedIds.set([]);
  }

  protected onBulkAction(actionId: string): void {
    this.bulkActionClick.emit({ actionId, selectedIds: this.selectedIds() });
  }

  // ── Sort / paging ────────────────────────────────────────────────────

  protected onSort(key: string): void {
    const col = this.columns().find((c) => c.key === key);
    if (!col?.sortable) return;
    const field = col.sortKey ?? col.key;
    const current = this.sort();
    const direction =
      current?.sortBy === field && current.sortDirection === 'ASC' ? 'DESC' : 'ASC';
    this.sortChange.emit({ sortBy: field, sortDirection: direction });
  }

  protected readonly canPrev = computed(() => this.page() > 0);
  protected readonly canNext = computed(() => this.page() < this.totalPages() - 1);

  protected readonly rangeFrom = computed(() =>
    this.totalElements() === 0 ? 0 : this.page() * this.pageSize() + 1,
  );
  protected readonly rangeTo = computed(() =>
    Math.min((this.page() + 1) * this.pageSize(), this.totalElements()),
  );

  protected prevPage(): void {
    if (this.canPrev()) this.pageChange.emit(this.page() - 1);
  }
  protected nextPage(): void {
    if (this.canNext()) this.pageChange.emit(this.page() + 1);
  }
  protected onPageSizeChange(value: string): void {
    // Changing the page size resets to the first page — the current offset is
    // meaningless under a new page size.
    this.pageSizeChange.emit(Number(value));
    if (this.page() !== 0) this.pageChange.emit(0);
  }

  // ── Tabs ─────────────────────────────────────────────────────────────

  protected onTab(id: string): void {
    if (id !== this.activeTabId()) this.tabChange.emit(id);
  }

  // ── Search ───────────────────────────────────────────────────────────

  protected onSearchInput(value: string): void {
    this.searchTerm.set(value);
  }

  // ── Density / column menu ────────────────────────────────────────────

  protected setDensity(d: GridDensity): void {
    this.density.set(d);
  }

  protected toggleColumnMenu(event: Event): void {
    event.stopPropagation();
    this.columnMenuOpen.update((v) => !v);
  }

  protected toggleColumn(key: string): void {
    const col = this.columns().find((c) => c.key === key);
    if (col?.alwaysVisible) return;
    const hidden = new Set(this.hiddenColumns());
    hidden.has(key) ? hidden.delete(key) : hidden.add(key);
    this.hiddenColumns.set(hidden);
  }

  // ── Rows: click, actions, overflow menu ──────────────────────────────

  // Click fires immediately — no dblclick-disambiguation delay. A double-click
  // therefore also runs the single-click behaviour first (panel opens, then the
  // parent navigates or opens its dialog on top), which is the native DOM
  // click/click/dblclick sequence and keeps selection feeling instant.
  protected onRowClick(row: T): void {
    this.rowClick.emit(row);
    if (this.detailEnabled()) {
      this.detailRow.set(row);
      this.openMenuRowId.set(null);
    }
  }

  protected onRowDblClick(row: T): void {
    this.rowDblClick.emit(row);
  }

  /** Inline row actions (placement !== 'menu'). */
  protected inlineActions(): GridRowAction<T>[] {
    return this.rowActions().filter((a) => a.placement !== 'menu');
  }
  /** Overflow-menu row actions (placement === 'menu'). */
  protected menuActions(): GridRowAction<T>[] {
    return this.rowActions().filter((a) => a.placement === 'menu');
  }

  /** Overflow actions resolved for one row into the ActionMenu's plain items:
   *  labelKey translated (grid scope, like inline actions) and the disabled
   *  predicate evaluated — the menu itself is context-free. */
  protected menuItemsFor(row: T): ActionMenuItem[] {
    const { t } = this.labels();
    return this.menuActions().map((a) => ({
      id: a.id,
      label: a.labelKey ? t(a.labelKey) : a.label,
      icon: a.icon,
      section: a.section,
      danger: a.danger,
      disabled: a.disabled ? a.disabled(row) : false,
    }));
  }

  protected onRowAction(actionId: string, row: T, event?: Event): void {
    event?.stopPropagation();
    this.openMenuRowId.set(null);
    this.rowActionClick.emit({ actionId, row });
  }

  protected setRowMenuOpen(id: string, open: boolean): void {
    // Trigger-click path (and closes): the menu belongs back at the ⋮.
    this.menuAnchor.set(null);
    this.openMenuRowId.set(open ? id : null);
  }

  /** Right-click: open the row's overflow menu at the cursor. Rows without
   *  menu actions keep the native context menu. */
  protected onRowContextMenu(id: string, event: MouseEvent): void {
    if (!this.menuActions().length) return;
    event.preventDefault();
    this.menuAnchor.set({ x: event.clientX, y: event.clientY });
    this.openMenuRowId.set(id);
  }

  protected closeDetail(): void {
    this.detailRow.set(null);
  }

  // ── CSV export (visible page) ────────────────────────────────────────

  protected onExport(): void {
    // Emit so a parent can override with a full-dataset server export; also do
    // the page-scoped client export by default.
    this.exportCsv.emit();
    this.exportVisiblePage();
  }

  /** Pure CSV builder over the current page + visible columns. Testable. */
  buildCsv(): string {
    const cols = this.visibleColumns();
    const esc = (v: unknown): string => {
      const s = v == null ? '' : String(v);
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.map((c) => esc(c.label)).join(',');
    const lines = this.data().map((row) =>
      cols.map((c) => esc(this.cellValue(c, row))).join(','),
    );
    return [header, ...lines].join('\r\n');
  }

  private exportVisiblePage(): void {
    const blob = new Blob([this.buildCsv()], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Translated chrome strings (re-evaluate on language switch) ───────

  protected readonly labels = computed(() => {
    this.activeLang();
    const t = (k: string, params?: Record<string, unknown>) =>
      this.transloco.translate(`grid.${k}`, params);
    return { t };
  });

  // ── Dismissal (click-outside + Escape) ───────────────────────────────

  protected onDocumentMouseDown(event: MouseEvent): void {
    const inside = this.hostEl.nativeElement.contains(event.target as Node);
    if (!inside) {
      this.openMenuRowId.set(null);
      this.columnMenuOpen.set(false);
    }
  }

  protected onEscape(): void {
    if (this.openMenuRowId()) {
      this.openMenuRowId.set(null);
    } else if (this.columnMenuOpen()) {
      this.columnMenuOpen.set(false);
    } else if (this.detailRow() && (this.detailMode() === 'overlay' || this.compactDetail())) {
      // The dismissable overlay and the compact sheet close on Escape; the
      // persistent split pane doesn't.
      this.detailRow.set(null);
    }
  }

  protected trackByRow = (_: number, r: RenderRow<T>) => r.id;
  protected trackByKey = (_: number, c: { key: string }) => c.key;
}
